import { useApolloClient } from "@apollo/client";
import { useEffect, useState } from "react";
import { z } from "zod";

import { ATTRIBUTION_VALUE_MAX_LENGTH } from "@medbillai/schemas/attribution";
import { Flag } from "@medbillai/utils";

import { captureUnreportedError } from "../../lib/apollo/error";
import { errorHandling } from "../../lib/apollo/utils";
import { useAuth } from "../../lib/auth";
import { useBoolVariation } from "../../lib/flag";
import { useFlagHasIdentified } from "../../lib/flag/shared";
import { getReferredSignupQuery } from "./queries";

// Typed as a string by codegen, but the JSON scalar arrives as an object.
// Only `referral` is checked, so another key can't hide a real referral.
const referralAttributionSchema = z.object({
  referral: z.string().max(ATTRIBUTION_VALUE_MAX_LENGTH),
});

const hasReferral = (attribution: unknown): boolean =>
  referralAttributionSchema.safeParse(attribution).success;

/**
 * Whether the signed-in user signed up through a referral link: `undefined`
 * until that user's flags and own attribution are read, and after a failed
 * read, so neither first-session modal opens on a guess; `false` with the
 * program off.
 */
export function useReferredSignup(): boolean | undefined {
  // Across a sign-in the flag client still holds the previous context, and
  // native reads it ungated, so the program can read off for a moment.
  const flagsIdentified = useFlagHasIdentified();
  const enabled = useBoolVariation(Flag.REFERRAL_PROGRAM, false);
  const userId = useAuth().user?.id;
  const client = useApolloClient();
  // Keyed by the user it was read for, so a previous user's late answer or
  // error never stands in for the current one's.
  const [answer, setAnswer] = useState<{ userId: string; referred: boolean }>();

  useEffect(() => {
    if (!flagsIdentified || !enabled || !userId) {
      return;
    }
    let current = true;
    const settle = (referred: boolean) => {
      if (current) {
        setAnswer({ userId, referred });
      }
    };
    client
      .query({
        query: getReferredSignupQuery,
        // A sign-in swaps users without clearing the cache, and an in-flight
        // read would be shared, so either could hand over the previous user's `me`.
        fetchPolicy: "no-cache",
        context: { ...errorHandling("caller"), queryDeduplication: false },
      })
      .then(
        ({ data }) => {
          if (data.me?.id === userId) {
            settle(hasReferral(data.me.attribution));
          }
        },
        (error: unknown) => {
          if (current) {
            captureUnreportedError(error, {
              tags: { feature: "referral-program" },
            });
          }
        },
      );
    return () => {
      current = false;
    };
  }, [client, flagsIdentified, enabled, userId]);

  if (!flagsIdentified) {
    return undefined;
  }
  if (!enabled) {
    return false;
  }
  return answer !== undefined && answer.userId === userId
    ? answer.referred
    : undefined;
}
